import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterBar, { defaultFilter, type FilterState } from '@/components/feature/FilterBar';
import Pagination from '@/components/feature/Pagination';
import MovieCard from '@/components/feature/MovieCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getListCategoryById, getListCategoriesByType, filterMovies } from '@/lib/movieUtils';
import { BRAND_NAME } from '@/mocks/categories';

const PAGE_SIZE = 8;

export default function ListPage() {
  const { categoryId = '' } = useParams();
  const category = getListCategoryById(categoryId);
  const [filter, setFilter] = useState<FilterState>(defaultFilter);
  const [page, setPage] = useState(1);

  usePageMeta({
    title: category
      ? `${category.name} - ${BRAND_NAME}高清影音资源集锦`
      : `资源列表 - ${BRAND_NAME}`,
    description: category
      ? `${category.description} 支持按地区、年份、评分筛选，浏览更多${category.typeName}国语影音资源。`
      : `${BRAND_NAME}资源列表页`,
    keywords: `${category?.name || '资源'},国语对白,影音资源,${category?.typeName || ''}`,
  });

  useEffect(() => {
    setFilter(defaultFilter);
    setPage(1);
  }, [categoryId]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const results = useMemo(
    () =>
      category
        ? filterMovies({ type: category.type, subCategory: category.subCategory, ...filter })
        : [],
    [category, filter],
  );

  const totalPages = Math.ceil(results.length / PAGE_SIZE);
  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const siblings = category ? getListCategoriesByType(category.type) : [];

  if (!category) {
    return (
      <PageShell>
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-24 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 text-xl font-bold text-foreground-900">未找到该资源分类</h1>
          <p className="mt-2 text-sm text-foreground-500">该细分分类不存在，请返回首页重新选择。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-10">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: category.typeName, to: `/category/${category.type}` },
            { label: category.name },
          ]}
        />

        <div className="mt-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{category.name}</h1>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-3xl">{category.description}</p>
        </div>

        <div className="mt-6 flex items-center gap-2 overflow-x-auto hide-scrollbar pb-1">
          {siblings.map((sub) => (
            <Link
              key={sub.id}
              to={`/list/${sub.id}`}
              className={`shrink-0 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                sub.id === category.id
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-500'
              }`}
            >
              {sub.name}
            </Link>
          ))}
        </div>

        <div className="mt-5">
          <FilterBar value={filter} onChange={setFilter} />
        </div>

        <div className="mt-6">
          <p className="text-sm text-foreground-500">
            共 <span className="text-foreground-900 font-semibold">{results.length}</span> 部资源
          </p>
        </div>

        {pageItems.length === 0 ? (
          <div className="py-20 text-center">
            <i className="ri-inbox-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-700">该分类下暂无符合筛选条件的资源</p>
            <button
              type="button"
              onClick={() => setFilter(defaultFilter)}
              className="mt-5 inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {pageItems.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-8">
            <Pagination current={page} total={totalPages} onChange={setPage} />
          </div>
        )}
      </div>
    </PageShell>
  );
}